import { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  navigateToCommunity,
  navigateToMember,
  navigateToSite,
} from '@flexo/resolvers';
import { BreadcrumbItem, HeaderType } from '../types';
import { useEntityData } from './useEntityData';

export const useBreadcrumbs = (type: HeaderType): BreadcrumbItem[] => {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const { getCommunityById, getSiteById, getMemberById, getAllCommunities } =
    useEntityData();
  const queryParams = window?.location?.search || '';

  return useMemo(() => {

    switch (type) {
      case 'communities':
        return [
          {
            label: t('widgets.header.communities'),
            isClickable: false,
          },
        ];

      case 'community':
        const member = getMemberById(params.id as string);
        const memberCommunityId = member?.communityId;
        const community =
          getCommunityById(memberCommunityId) || getAllCommunities()[0];

        return [
          {
            label: t('widgets.header.communities'),
            onClick: () => navigate(`/communities${queryParams}`),
            isClickable: true,
          },
          {
            type: 'communities-dropdown',
            isClickable: false,
          },
        ];

      case 'member': {
        const member = getMemberById(params.id as string);
        const memberCommunityId = member?.communityId;
        const community =
          getCommunityById(memberCommunityId) || getAllCommunities()[0];

        return [
          {
            label: t('widgets.header.communities'),
            onClick: () => navigate(`/communities${queryParams}`),
            isClickable: true,
          },
          {
            label: community?.communityName || 'Community name not found',
            onClick: () =>
              navigateToCommunity('community', community?.communityId),
            isClickable: true,
          },
          {
            type: 'members-dropdown',
            isClickable: false,
          },
        ];
      }

      case 'member-data-vis': {
        const member = getMemberById(params.id as string);
        const memberCommunityId = member?.communityId;
        const community =
          getCommunityById(memberCommunityId) || getAllCommunities()[0];
        const memberName =
          !member?.memberFirstName && !member?.memberLastName
          ? 'Member name not found'
          : `${member?.memberFirstName || ''} ${member?.memberLastName || ''}`.trim();

        return [
          {
            label: community?.communityName || 'Community name not found',
            onClick: () =>
              navigateToCommunity('community', community?.communityId),
            isClickable: true,
          },
          {
            label: memberName,
            onClick: () => navigateToMember('member', params.id as string),
            isClickable: true,
          },
        ];
      }

      case 'site': {
        const site = getSiteById(params.id as string);
        const member = getMemberById(site?.memberId as string);
        const community =
          getCommunityById(member?.communityId || site?.communityId) || getAllCommunities()[0];
        const siteName =
          site?.pod || site?.name || site?.siteName || 'Site name not found';

        return [
          {
            label:
              `${member?.memberFirstName || ''} ${
                member?.memberLastName || ''
              }`.trim() || 'Member name not found',
            onClick: () => navigateToMember('member', member?.memberId),
            isClickable: true,
          },
          {
            label: siteName,
            onClick: () => navigateToSite('site', params.id as string),
            isClickable: true,
          },
        ];
      }

      case 'site-overview': {
        const site = getSiteById(params.id as string);
        const member = getMemberById(site?.memberId as string);
        const community =
          getCommunityById(member?.communityId || site?.communityId) || getAllCommunities()[0];

        return [
          {
            label: community?.communityName || 'Community name not found',
            onClick: () =>
              navigateToCommunity('community', community?.communityId),
            isClickable: true,
          },
          {
            label:
              `${member?.memberFirstName || ''} ${
                member?.memberLastName || ''
              }`.trim() || 'Member name not found',
            onClick: () => navigateToMember('member', member?.memberId),
            isClickable: true,
          },
          {
            type: 'sites-dropdown',
            isClickable: false,
          },
        ];
      }

      case 'community-data-vis': {
        const communities = getAllCommunities();
        const community =
          communities.find(
            (c: any) => (c?.communityId || c?.flexoCommunityId) === params.id
          ) || communities[0];

        return [
          {
            label: t('widgets.header.communities'),
            onClick: () => navigate(`/communities${queryParams}`),
            isClickable: true,
          },
          {
            label: community?.communityName || 'Community name not found',
            onClick: () =>
              navigateToCommunity('community', params.id as string),
            isClickable: true,
          },
        ];
      }

      default:
        return [];
    }
  }, [
    type,
    params,
    getCommunityById,
    getSiteById,
    getMemberById,
    getAllCommunities,
    t,
    navigate,
    queryParams,
  ]);
};
